import Phaser from "phaser";
import type { Element } from "../model/board";
import { COLORS } from "./palette";

type Spark = { root: Phaser.GameObjects.Container; halo: Phaser.GameObjects.Image;
  chip: Phaser.GameObjects.Image; x: number; y: number; phase: number; bright: boolean };

// These painted sparks stay until spent: three small ones are one attack, each big one is another.
export class ChargeView {
  readonly root: Phaser.GameObjects.Container;
  private active: Spark[] = [];
  private all = new Set<Spark>();
  private count = 0;
  private radius: number;
  constructor(private scene: Phaser.Scene, private element: Element, height: number,
    private phase: number, private motion: boolean, private body: Phaser.GameObjects.Container) {
    // Stay above neighboring characters, while following this character's moving body.
    this.root = scene.add.container(0, 0).setDepth(505);
    this.radius = Math.max(28, Math.min(70, height * 0.45));
    this.focusY = -height / 2;
    this.follow();
  }
  private focusY: number;
  private follow(): void {
    const point = this.body.getWorldTransformMatrix().transformPoint(0, this.focusY);
    this.root.setPosition(point.x, point.y);
  }
  setCount(count: number, animate = true): void {
    if (count === this.count) return;
    this.count = count;
    const total = count > 0 ? count + 2 : 0;
    while (this.active.length > total) this.spend(this.active.pop()!, animate && this.motion);
    while (this.active.length < total) this.add(this.active.length, animate && this.motion);
  }
  private add(index: number, animate: boolean): void {
    const bright = index >= 3, r = this.radius;
    const positions = [[-r-12,-6],[-r,-6],[-r-6,7],[r+8,-10],[r+10,18],[-r-6,-30],[r+4,-39]];
    const [x, y] = positions[index];
    const halo = this.scene.add.image(0, 0, "fx-glow").setDisplaySize(bright ? 48 : 27, bright ? 48 : 27)
      .setTint(COLORS[this.element].body).setAlpha(bright ? 0.85 : 0.5).setBlendMode(Phaser.BlendModes.ADD);
    const chip = this.scene.add.image(0, 0, `fx-${this.element}`).setDisplaySize(bright ? 38 : 23, bright ? 38 : 23);
    const core = this.scene.add.circle(0, 0, bright ? 2.2 : 1.1, 0xfffcee, bright ? 0.95 : 0.65);
    const root = this.scene.add.container(x, y, [halo, chip, core]);
    const spark = { root, halo, chip, x, y, bright, phase: this.phase + index * 1.47 };
    this.root.add(root); this.active.push(spark); this.all.add(spark);
    if (animate) {
      root.setScale(0.15);
      this.scene.tweens.add({ targets: root, scale: 1, duration: 240, ease: "Back.Out" });
    }
  }
  private spend(spark: Spark, animate: boolean): void {
    this.scene.tweens.killTweensOf(spark.root);
    const remove = () => { this.all.delete(spark); spark.root.destroy(); };
    if (!animate) { remove(); return; }
    // Pull the spent energy into the character rather than just blinking it away.
    this.scene.tweens.add({ targets: spark.root, x: 0, y: 0, scale: 0.12, alpha: 0,
      duration: 170, ease: "Cubic.In", onComplete: remove });
  }
  update(time: number): void {
    this.follow();
    if (!this.motion) return;
    this.active.forEach(spark => {
      const wave = Math.sin(time / 420 + spark.phase);
      spark.root.setPosition(spark.x + Math.cos(time / 670 + spark.phase) * 1.5, spark.y + wave * 2.5);
      spark.halo.setAlpha((spark.bright ? 0.8 : 0.48) + wave * 0.12);
      spark.chip.setAngle(Math.sin(time / 590 + spark.phase) * 13);
    });
  }
  destroy(): void {
    this.all.forEach(spark => this.scene.tweens.killTweensOf(spark.root));
    this.all.clear(); this.active = []; this.root.destroy();
  }
}
